import clsx from "clsx";
import { type FC, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  usePresentation,
  useSelectedPageListItemColoring,
} from "../../../../../utils/presentation";

import { IconFromPage } from "./IconFromPage";
import { PageSubmissionStatus } from "./PageSubmissionStatus";

import { PAGE_SCROLL_CONTAINER_ID } from "components/constants";
import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { showPageDisabled } from "components/materials/page/groups/helpers";
import { PageTableOfContents } from "components/materials/sidebar/PageTableOfContents";
import { PageSidebarOptionsMenu } from "components/menus/PageSidebarOptionsMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { DragAndDrop, DraggableItemType } from "mds/components/DragAndDrop";
import { ListItem, ListItemProps } from "mds/components/ListItem";
import { EyeNotVisibleIcon, LockLockedIcon, PresentPlayBackgroundIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentPageId,
  selectCurrentSectionIsLinked,
  selectCurrentSectionIsTemplate,
  selectIsEditModeEnabled,
  selectSectionTitleBySectionId,
  selectSelectedPageTabVariant,
} from "store/selectors";
import { onTitleChanged } from "utils/store-utils";

type PageListItemProps = {
  pageIndex: number;
  page: PageType;
  className?: string;
  containerId?: string;
  topicId?: string;
  kind?: ListItemProps["kind"];
  alwaysShowDueDates?: boolean;
  showSectionTitle?: boolean;
  lastItem?: boolean;
  onPageClick: () => void;
  onMovePageLocally?: (item: DraggableItemType, toIndex: number, dropContainerId: string) => void;
  onDropPage?: (item: DraggableItemType, index: number, sourceContainerId: string) => void;
  onEditingTitle?: (isEditing: boolean) => void;
};

/*
 * This is intended to be used for displaying a Page in a list anywhere in the app (e.g.
 * Course page or various sidebars), currently only implemented for sidebar Pages within
 * a Topic. Note that the topicId will be undefined if the Page is not contained within a Topic.
 */
export const PageListItem: FC<PageListItemProps> = ({
  className,
  pageIndex,
  page,
  containerId,
  topicId,
  kind,
  alwaysShowDueDates = false,
  lastItem = false,
  showSectionTitle = false,
  onMovePageLocally,
  onDropPage,
  onPageClick,
  onEditingTitle,
}) => {
  const selectedPageId = useAppSelector(selectCurrentPageId);
  const { canAuthorSection } = useCapabilities();
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const selectedListItemColoring = useSelectedPageListItemColoring();
  const { presentedPageId } = usePresentation();
  const selectedPageTabVariant = useAppSelector(selectSelectedPageTabVariant);
  const sectionTitle = useAppSelector((s) => selectSectionTitleBySectionId(s, page.section_id));
  const canEdit = isEditModeEnabled;
  const canUpdatePage = canAuthorSection && isEditModeEnabled;
  const isSelected = selectedPageId === page.id;
  const canSeeTableOfContents = isSelected && selectedPageTabVariant === "responses";
  const showPageAsUnreleased = showPageDisabled(page) && !isInTemplateSection;

  // TODO: Only show the table of contents if we are in the class responses view.
  // const isClassResponsesView = useAppSelector(selectIsClassResponsesView);

  const handleEditingTitle = (isEditing: boolean) => {
    setIsEditingTitle(isEditing);
    if (onEditingTitle) {
      onEditingTitle(isEditing);
    }
  };

  const onClick = useCallback(() => {
    if (isSelected) {
      // Clicking on the selected list item should also scroll it to the top.
      document.getElementById(PAGE_SCROLL_CONTAINER_ID)?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    onPageClick();
  }, [onPageClick, isSelected]);

  return (
    <div>
      <DragAndDrop
        draggable={canUpdatePage && !isEditingTitle && !!onDropPage}
        dragItemKind="page"
        dropContainerId={containerId}
        dropIndex={pageIndex}
        droppable={canUpdatePage && !!onDropPage}
        item={page}
        key={page.id}
        onDraggedItemHover={onMovePageLocally}
        onDropItem={onDropPage}
      >
        <ListItem
          className={twMerge(
            "flex w-full items-center justify-between gap-2",
            kind !== "primary" && "pr-[3px]",
            showSectionTitle && "h-[59px]",
            className,
          )}
          eventName="Page selected"
          eventProperties={{ pageTitle: page.title, pageId: page.id }}
          isSelected={isSelected}
          isUnreleased={!isSelected && showPageAsUnreleased}
          kind={kind}
          label={page.title}
          lastItem={lastItem}
          selectedColoring={presentedPageId ? selectedListItemColoring : undefined}
          onClick={onClick}
        >
          <HoverableToolbar
            className="min-w-12 flex-grow"
            contentClassName="bg-black-tint-97"
            disableUnlessEditMode={!isInLinkedSection}
            readOnly={!canUpdatePage && !isInLinkedSection}
            reference="page"
            showEditButton={!isInLinkedSection}
            onEditModeChange={handleEditingTitle}
          >
            <div className="flex w-full min-w-12 flex-row items-center justify-between pr-1">
              <div
                className={clsx(
                  "flex w-full min-w-12 flex-col gap-0",
                  showSectionTitle && "py-[10px]",
                )}
              >
                {showSectionTitle && <div className="min-w-0 truncate text-xs">{sectionTitle}</div>}

                <div
                  className={clsx("flex w-full min-w-12 items-center gap-2 font-medium", {
                    "text-black-tint-55": !isSelected && showPageAsUnreleased,
                  })}
                >
                  <IconFromPage
                    isInTemplateSection={isInTemplateSection}
                    isSelected={isSelected}
                    page={page}
                  />

                  {/*  TODO: The page-title class intends to restrict the max width, but it doesn't take into account
                      the new lock/due date items on pages, which causes the content to get expanded wider than it should */}
                  <HoverableEditField
                    className="page-title flex-grow"
                    label={t("glossary.page_title")}
                    textSize={kind === "primary" ? "m" : "s"}
                    value={page.title}
                    disableUnlessEditMode
                    onValueChanged={(newTitle) => onTitleChanged("pages", page.id, newTitle)}
                  />
                </div>
              </div>

              {presentedPageId && page.id === presentedPageId && (
                <PresentPlayBackgroundIcon className="text-green group-data-[selected]/listitem:text-white" />
              )}

              {page.locked_at && <LockLockedIcon className="ml-1" />}
              {showPageAsUnreleased && <EyeNotVisibleIcon className="ml-1" />}
            </div>

            <PageSidebarOptionsMenu
              currentTopicId={topicId}
              editMode={canUpdatePage && canEdit}
              page={page}
            />
          </HoverableToolbar>

          <PageSubmissionStatus alwaysShowDueDates={alwaysShowDueDates} page={page} />
        </ListItem>
      </DragAndDrop>

      {canSeeTableOfContents && <PageTableOfContents />}
    </div>
  );
};
